import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Error404 } from '@/components/custom/error';
import usePermission from '@/hooks/usePermission';
import { Link } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { t } from 'i18next';
import { useClient } from 'jsonapi-react';
import { BulletinBoardsRow } from './bulletin-boards-row';
import { BulletinBoardsModal } from './bulletin-boards-modal';
import { Filter, FilterActions, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import EndScroll from '@/app/components/endScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DashboardContent } from '@/layouts/dashboard';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableHeaderWrapper } from '@/components/ui/table';

export function BulletinBoards(props) {
  const { uri } = props;
  const client = useClient();
  const [bannersList, setBannersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const isInitialMount = useRef(true);

  const pageSize = 10;
  const [hasMore, setHasMore] = useState(true);
  const debounceTimer = useRef(null);

  const viewBanners = usePermission('Visualizar Mural', 'Mural');

  const filters = useFormik({
    initialValues: {
      searchTerm: '',
      profile: '',
      status: ''
    }
  });

  const getBannersList = async () => {
    let url = `bulletin_boards?page[number]=${pageNumber}&page[size]=${pageSize}&direction=desc&sort=created_at`;

    if (filters.values.searchTerm) {
      url += `&q=${filters.values.searchTerm}`;
    }
    if (filters.values.profile && filters.values.profile !== 'blank') {
      url += `&filter[profiles.id]=${filters.values.profile}`;
    }
    if (filters.values.status && filters.values.status !== 'blank') {
      url += `&filter[published]=${filters.values.status === 'published' ? true : false}`;
    }

    setLoading(true);
    try {
      const response = await client.fetch(url);
      setBannersList(pageNumber === 1 ? response.data : [...bannersList, ...response.data]);

      if (response.data.length < pageSize) {
        setHasMore(false);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getProfilesList = async () => {
    try {
      const { data, error } = await client.fetch('/profiles');
      if (error) {
        toast.error(t('warning.errorFetchingProfiles'));
      } else {
        setProfiles(data);
      }
    } catch (error) {
      toast.error(t('warning.errorFetchingProfiles'));
    }
  };

  useEffect(() => {
    getProfilesList();
  }, []);

  useEffect(() => {
    getBannersList();
  }, [pageNumber]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (pageNumber === 1) {
        getBannersList();
      } else {
        setPageNumber(1);
      }
      setHasMore(true);
      setBannersList([]);
    }, 500);

    return () => clearTimeout(debounceTimer.current);
  }, [filters.values]);

  const breadcrumbs = {
    title: 'Banners',
    nav: [
      {
        route: uri,
        name: 'Banners informativos',
        isActive: true
      }
    ]
  };

  if (!viewBanners) return <Error404 />;

  const updateBannerStatus = (id, published) => {
    setBannersList(prev => prev.map(b => (b.id === id ? { ...b, 'published-at': published } : b)));
  };

  const TABLE_HEAD = [
    { label: 'Título do banner', width: 350 },
    { label: 'Período', width: 200 },
    { label: 'Perfis atribuídos', width: 250 },
    { label: 'Publicação', width: 150 },
    { label: 'Ações', width: 80 }
  ];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Filter variant="plain">
        <FilterContent>
          <FilterInput
            label={t('textsCommon.research')}
            placeholder={t('bulletinBoards.searchBanner')}
            type="search"
            name="search"
            onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
          />
          <FilterSelectNative
            label={t('bulletinBoards.associateProfiles')}
            value={filters.values.profile}
            onChange={e => filters.setFieldValue('profile', e.target.value)}
            options={profiles.map(item => ({
              id: item.id,
              name: item.name
            }))}
          />
          <FilterSelectNative
            label="Status"
            value={filters.values.status}
            onChange={e => filters.setFieldValue('status', e.target.value)}
            options={[
              { id: 'published', name: 'Publicado' },
              { id: 'unpublished', name: 'Não publicado' }
            ]}
          />
        </FilterContent>

        {usePermission('Criar Mural', 'Mural') && (
          <FilterActions>
            <Button
              wide
              asChild
            >
              <Link to={`/banners/novo`}>{t('bulletinBoards.newBanner')}</Link>
            </Button>
          </FilterActions>
        )}
      </Filter>

      {!bannersList && loading && <Loader />}
      {!loading && bannersList && bannersList?.length === 0 && <EmptyState type="data" />}

      <InfiniteScroll
        dataLength={bannersList.length}
        next={() => {
          setPageNumber(pageNumber + 1);
        }}
        hasMore={hasMore}
        loader={<Loader />}
        endMessage={<EndScroll />}
      >
        <Table style={{ minWidth: '990px' }}>
          <TableHeaderWrapper tableHead={TABLE_HEAD} />

          <TableBody>
            {bannersList.map(item => (
              <BulletinBoardsRow
                key={item.id}
                banner={item}
                setOpenDetailsModal={setOpenDetailsModal}
                setSelectedBanner={setSelectedBanner}
                updateBannerStatus={updateBannerStatus}
                updateList={() => {
                  setBannersList(prev => prev.filter(b => b.id !== item.id));
                }}
              />
            ))}
          </TableBody>
        </Table>
      </InfiniteScroll>

      <BulletinBoardsModal
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        banner={selectedBanner}
      />
    </DashboardContent>
  );
}
